import { Injectable, inject, signal } from "@angular/core";
// Deep imports: avoid the barrel so we don't drag CastOffProApp / renderer
// into the initial chunk. See `sim.service.ts` for the lazy load.
import { cloneAppState, defaultAppState } from "../../../simulation-core/State";
import type { AppState } from "../../../simulation-core/State";
import type { AppStatePatch } from "../../../simulation-core";
import { SimService } from "./sim.service";

/**
 * Reactive snapshot of the live `AppState`. Components write through `patch`,
 * which forwards to the sim and updates the local signal so OnPush views
 * re-render.
 *
 * NOTE: the simulation mutates its own state object continuously (e.g. boat
 * pose, mooring tensions). This store does NOT mirror that — it tracks only
 * UI-driven fields (wind, water level, camera mode, mooring targets). For
 * live telemetry (SOG, HDG, …) we'll add a separate `TelemetryService` that
 * polls the handle from a rAF effect.
 */
@Injectable({ providedIn: "root" })
export class AppStateStore {
  private readonly sim = inject(SimService);

  readonly state = signal<AppState>(cloneAppState(defaultAppState()));

  patch(patch: AppStatePatch): void {
    this.sim.patchState(patch);
    // Single shallow merge; defensively copy only the dictionaries the
    // patch actually touched (the rest can share references — signal
    // identity is enough to trigger consumers).
    this.state.update((current) => ({
      ...current,
      ...patch,
      mooringTargets: patch.mooringTargets
        ? { ...patch.mooringTargets }
        : current.mooringTargets,
      mooringRestLengths: patch.mooringRestLengths
        ? { ...patch.mooringRestLengths }
        : current.mooringRestLengths,
    }));
  }

  /** Pull a fresh snapshot from the live handle, e.g. after attach. */
  syncFromHandle(): void {
    const live = this.sim.liveState();
    if (live) this.state.set(cloneAppState(live));
  }
}
